import { useEffect, useState, useContext } from 'react';
import { UserContext } from '../../UserContext';
import { 
    CalendarDaysIcon, 
    ClockIcon, 
    MapPinIcon,
    UserCircleIcon 
} from '@heroicons/react/24/outline';

/**
 * Componente WelcomeBanner - Barra de bienvenida del Dashboard
 * Muestra saludo personalizado, fecha, hora en tiempo real y sucursal activa
 * 
 * @component
 * @example
 * return (
 *   <WelcomeBanner />
 * )
 */
const WelcomeBanner = () => {
    const { user, sucursal } = useContext(UserContext);
    const [fechaActual, setFechaActual] = useState(new Date());
    const [horaActual, setHoraActual] = useState(new Date());

    // Actualizar hora cada segundo
    useEffect(() => {
        const timer = setInterval(() => {
            setHoraActual(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    // Actualizar fecha cada minuto
    useEffect(() => {
        const timer = setInterval(() => {
            setFechaActual(new Date());
        }, 60000);

        return () => clearInterval(timer);
    }, []);

    /**
     * Formatea la fecha en formato largo español
     * @param {Date} fecha - Fecha a formatear
     * @returns {string} Fecha formateada: "Martes, 20 de Mayo 2026"
     */
    const formatearFecha = (fecha) => {
        const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        
        const diaSemana = dias[fecha.getDay()];
        const dia = fecha.getDate();
        const mes = meses[fecha.getMonth()];
        const año = fecha.getFullYear();
        
        return `${diaSemana}, ${dia} de ${mes} ${año}`;
    };

    /**
     * Formatea la hora en formato 12 horas con segundos
     * @param {Date} fecha - Fecha con hora a formatear
     * @returns {string} Hora formateada: "10:45:32 AM"
     */
    const formatearHora = (fecha) => {
        return fecha.toLocaleTimeString('es-MX', { 
            hour: '2-digit', 
            minute: '2-digit', 
            second: '2-digit',
            hour12: true 
        });
    };

    /**
     * Obtiene el saludo apropiado según la hora del día
     * @returns {string} Saludo: "¡Buenos días", "¡Buenas tardes" o "¡Buenas noches"
     */
    const obtenerSaludo = () => {
        const hora = horaActual.getHours();
        if (hora >= 6 && hora < 12) return '¡Buenos días';
        if (hora >= 12 && hora < 20) return '¡Buenas tardes';
        return '¡Buenas noches';
    };

    return (
        <div className="bg-gradient-to-r from-[#0033ff] via-[#0066ff] to-[#11cdef] rounded-xl shadow-2xl overflow-hidden mb-8 relative">
            {/* Efecto de brillo decorativo */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none"></div>
            
            <div className="px-8 py-6 relative z-10">
                {/* Saludo y Usuario */}
                <div className="flex items-center space-x-4 mb-4">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                        <UserCircleIcon className="h-12 w-12 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-white">
                            {obtenerSaludo()}, {user?.name || 'Usuario'}!
                        </h1>
                        <p className="text-blue-100 text-sm mt-1">
                            Bienvenido al sistema de gestión Dento Metric
                        </p>
                    </div>
                </div>

                {/* Información Contextual - Indicadores */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    {/* Fecha */}
                    <div className="bg-white rounded-lg px-4 py-3 flex items-center space-x-3 shadow-md hover:shadow-lg transition-all duration-300">
                        <div className="bg-gradient-to-br from-[#0033ff] to-[#0066ff] rounded-lg p-2">
                            <CalendarDaysIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs font-medium">Fecha</p>
                            <p className="text-[#0033ff] font-bold text-sm">
                                {formatearFecha(fechaActual)}
                            </p>
                        </div>
                    </div>

                    {/* Hora */}
                    <div className="bg-white rounded-lg px-4 py-3 flex items-center space-x-3 shadow-md hover:shadow-lg transition-all duration-300">
                        <div className="bg-gradient-to-br from-[#0066ff] to-[#11cdef] rounded-lg p-2">
                            <ClockIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs font-medium">Hora</p>
                            <p className="text-[#0066ff] font-bold text-sm font-mono">
                                {formatearHora(horaActual)}
                            </p>
                        </div>
                    </div>

                    {/* Sucursal */}
                    <div className="bg-white rounded-lg px-4 py-3 flex items-center space-x-3 shadow-md hover:shadow-lg transition-all duration-300">
                        <div className="bg-gradient-to-br from-[#11cdef] to-[#0033ff] rounded-lg p-2">
                            <MapPinIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-gray-500 text-xs font-medium">Sucursal Asignada</p>
                            <p className="text-[#11cdef] font-bold text-sm truncate">
                                {sucursal || 'No seleccionada'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Barra decorativa inferior con animación */}
            <div className="h-2 bg-gradient-to-r from-[#11cdef] via-[#0066ff] to-[#0033ff] relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
            </div>
        </div>
    );
};

export default WelcomeBanner;
